import {
  Supplier,
  SupplierCertStatus,
  SupplierLocationView,
  SupplierSkuLinkView,
  SupplierStatus,
} from './supplierTypes';

export interface ApiSupplierLocation {
  locationId?: string | null;
  address?: string | null;
  postcode?: string | null;
  country?: string | null;
}

export interface ApiSupplierSkuLink {
  inventorySkuId?: string | null;
  skuCode?: string | null;
  productName?: string | null;
  supplierSkuCode?: string | null;
  leadTimeMeanDays?: number | null;
  leadTimeStdDays?: number | null;
  minimumOrderQuantity?: number | null;
  orderMultiple?: number | null;
  orderMultipleUnit?: string | null;
  incoterms?: string | null;
  isPreferred?: boolean | null;
  status?: string | null;
}

export interface ApiSupplier {
  id: string;
  supplierId: string;
  name: string;
  type?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactNumber?: string | null;
  hqId?: string | null;
  hqName?: string | null;
  locations?: ApiSupplierLocation[] | null;
  suppliedSkus?: ApiSupplierSkuLink[] | null;
  sourceType?: string | null;
  brandsSupplied?: string[] | null;
  coverageBranches?: string[] | null;
  certificationStatus?: string | null;
  certificationLabel?: string | null;
  certificationExpiry?: string | null;
  lifecycleStatus?: string | null;
  readinessGaps?: number | null;
  deletedAt?: string | null;
}

const CERT_STATUSES: SupplierCertStatus[] = [
  'valid',
  'expiring',
  'expired',
  'none',
];

const STATUSES: SupplierStatus[] = [
  'prospect',
  'onboarding',
  'approved',
  'active',
  'probation',
  'suspended',
  'terminated',
];

const toCertStatus = (value?: string | null): SupplierCertStatus =>
  CERT_STATUSES.includes(value as SupplierCertStatus)
    ? (value as SupplierCertStatus)
    : 'none';

const toStatus = (value?: string | null): SupplierStatus =>
  STATUSES.includes(value as SupplierStatus)
    ? (value as SupplierStatus)
    : 'active';

const toLocationView = (
  location: ApiSupplierLocation
): SupplierLocationView => ({
  locationId: location.locationId ?? '',
  address: location.address ?? '',
  postcode: location.postcode ?? '',
  country: location.country ?? '',
});

const toSkuLinkView = (link: ApiSupplierSkuLink): SupplierSkuLinkView => ({
  inventorySkuId: link.inventorySkuId ?? null,
  skuCode: link.skuCode ?? '',
  productName: link.productName ?? '',
  supplierSkuCode: link.supplierSkuCode ?? '',
  leadTimeMeanDays: link.leadTimeMeanDays ?? null,
  leadTimeStdDays: link.leadTimeStdDays ?? null,
  minimumOrderQuantity: link.minimumOrderQuantity ?? null,
  orderMultiple: link.orderMultiple ?? null,
  orderMultipleUnit: link.orderMultipleUnit ?? '',
  incoterms: link.incoterms ?? '',
  isPreferred: link.isPreferred ?? null,
  status: link.status ?? '',
});

export const apiSupplierToViewModel = (api: ApiSupplier): Supplier => {
  const gaps = api.readinessGaps ?? 0;
  const isEnriched = !!(
    api.sourceType ||
    api.lifecycleStatus ||
    api.brandsSupplied?.length
  );
  return {
    id: api.id,
    supplierId: api.supplierId,
    name: api.name,
    type: api.type ?? '',
    contactName: api.contactName ?? '',
    contactEmail: api.contactEmail ?? '',
    contactNumber: api.contactNumber ?? '',
    hqId: api.hqId ?? '',
    hqName: api.hqName ?? '',
    locations: (api.locations ?? []).map(toLocationView),
    suppliedSkus: (api.suppliedSkus ?? []).map(toSkuLinkView),
    sourceType: api.sourceType ?? '',
    brands: api.brandsSupplied ?? [],
    coverage: api.coverageBranches ?? [],
    certStatus: toCertStatus(api.certificationStatus),
    certLabel: api.certificationLabel ?? null,
    certExpiry: api.certificationExpiry ?? null,
    readiness: isEnriched ? { ready: gaps === 0, gaps } : null,
    status: toStatus(api.lifecycleStatus),
    isArchived: !!api.deletedAt,
  };
};
